import { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Grid,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import { createPreregistration, extractApiError, rememberReservation } from '../api.js';

const INITIAL = {
  last_name: '',
  first_name: '',
  email: '',
  phone: '',
  adults: 1,
  children: 0,
  comment: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i;

/** Nom : majuscules uniquement (lettres, espaces, tirets, apostrophes). */
function formatLastName(raw) {
  return String(raw || '')
    .normalize('NFC')
    .replace(/[^\p{L}\s'\-]/gu, '')
    .toLocaleUpperCase('fr-FR');
}

/** Prénom : majuscule en tête de chaque mot (espaces / tirets). */
function formatFirstName(raw) {
  const cleaned = String(raw || '')
    .normalize('NFC')
    .replace(/[^\p{L}\s'\-]/gu, '');
  return cleaned
    .toLocaleLowerCase('fr-FR')
    .replace(/(^|[\s\-'])\p{L}/gu, (ch) => ch.toLocaleUpperCase('fr-FR'));
}

/** Email : minuscules, sans espaces. */
function formatEmail(raw) {
  return String(raw || '').replace(/\s+/g, '').toLowerCase();
}

/**
 * Téléphone FR : chiffres seuls, format « 06 12 34 56 78 ».
 * Accepte un collage +33… → 0…
 */
function formatPhone(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('33') && digits.length >= 11) {
    digits = `0${digits.slice(2)}`;
  }
  digits = digits.slice(0, 10);
  const parts = digits.match(/.{1,2}/g);
  return parts ? parts.join(' ') : '';
}

function phoneDigits(formatted) {
  return String(formatted || '').replace(/\D/g, '');
}

/**
 * Formate une deadline ISO en libellé court "vendredi 17 octobre 2026 à 12:00".
 */
function formatDeadline(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('fr-FR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Renvoie le nombre de jours entiers avant la deadline (peut être négatif). */
function daysUntil(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = d.getTime() - Date.now();
  return Math.ceil(diffMs / (24 * 3600 * 1000));
}

export default function ReservationForm({ onCreated, capacity }) {
  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Complet dès que le total atteint la capacité. Une demande commencée
  // en dessous reste envoyable, même si elle fait dépasser.
  const full = Boolean(capacity?.is_full);

  // Date limite : bandeau d'avertissement à J-7 ; formulaire désactivé après.
  const deadline = capacity?.reservation_deadline ?? null;
  const closed = Boolean(capacity?.reservations_closed);
  const daysLeft = daysUntil(deadline);
  const showDeadlineWarning =
    !closed && deadline && daysLeft !== null && daysLeft >= 0 && daysLeft <= 7;

  // Activation du bouton "Préréserver" : champs formatés + email / tél. FR valides.
  const isFormValid = useMemo(() => {
    const adults = Number(form.adults);
    const children = Number(form.children);
    return (
      form.last_name.trim().length > 0 &&
      form.first_name.trim().length > 0 &&
      EMAIL_RE.test(form.email.trim()) &&
      phoneDigits(form.phone).length === 10 &&
      Number.isFinite(adults) && adults >= 1 &&
      Number.isFinite(children) && children >= 0
    );
  }, [form]);

  function handleChange(field) {
    return (e) => {
      let v = e.target.value;
      if (field === 'last_name') v = formatLastName(v);
      else if (field === 'first_name') v = formatFirstName(v);
      else if (field === 'email') v = formatEmail(v);
      else if (field === 'phone') v = formatPhone(v);
      setForm((f) => ({ ...f, [field]: v }));
    };
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const adults = Number(form.adults);
    const children = Number(form.children);
    if (!Number.isFinite(adults) || adults < 1) {
      setError('Le nombre d’adultes doit être au moins 1.');
      return;
    }
    if (!Number.isFinite(children) || children < 0) {
      setError('Le nombre d’enfants doit être positif.');
      return;
    }

    if (!EMAIL_RE.test(form.email.trim())) {
      setError('Adresse email invalide.');
      return;
    }
    if (phoneDigits(form.phone).length !== 10) {
      setError('Téléphone invalide : 10 chiffres attendus (ex. 06 12 34 56 78).');
      return;
    }

    setSubmitting(true);
    try {
      const created = await createPreregistration({
        last_name: formatLastName(form.last_name).trim(),
        first_name: formatFirstName(form.first_name).trim(),
        email: formatEmail(form.email).trim(),
        phone: formatPhone(form.phone).trim(),
        adults,
        children,
        comment: form.comment.trim() || null,
      });
      setSuccess(
        `Préréservation enregistrée pour ${created.first_name} ${created.last_name} — ${created.meals} repas. Un email de confirmation a été envoyé à ${created.email}.`,
      );
      // On mémorise localement (email + UUID) pour proposer un accès direct
      // au récap la prochaine fois que l'utilisateur revient sur le site.
      rememberReservation(created);
      setForm(INITIAL);
      onCreated?.(created);
    } catch (err) {
      setError(extractApiError(err, 'Impossible d’enregistrer la préréservation.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Paper elevation={0} component="form" onSubmit={handleSubmit} sx={{ mt: 2, p: { xs: 2, md: 2.5 }, border: 1, borderColor: 'divider' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Je préréserve mes repas
      </Typography>

      {closed && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Les préréservations en ligne sont closes</strong>{deadline ? ` depuis le ${formatDeadline(deadline)}` : ''}.
          Si vous souhaitez encore venir, contactez directement le club par téléphone : nous pouvons enregistrer votre place manuellement selon les disponibilités.
        </Alert>
      )}

      {showDeadlineWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {daysLeft === 0
            ? <><strong>Dernier jour pour préréserver !</strong> Clôture aujourd'hui à {new Date(deadline).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}.</>
            : <><strong>Plus que {daysLeft} jour{daysLeft > 1 ? 's' : ''}</strong> pour préréserver — clôture le {formatDeadline(deadline)}.</>}
        </Alert>
      )}

      {!closed && full && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Nous sommes complet.</strong> Plus de réservation possible.
          Si des places se libèrent, les inscriptions rouvriront automatiquement.
        </Alert>
      )}

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Nom"
            required
            fullWidth
            value={form.last_name}
            onChange={handleChange('last_name')}
            inputProps={{ maxLength: 80, style: { textTransform: 'uppercase' } }}
            helperText="En majuscules"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Prénom"
            required
            fullWidth
            value={form.first_name}
            onChange={handleChange('first_name')}
            inputProps={{ maxLength: 80 }}
            helperText="Première lettre de chaque mot en majuscule"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Email"
            type="email"
            required
            fullWidth
            value={form.email}
            onChange={handleChange('email')}
            error={form.email.length > 0 && !EMAIL_RE.test(form.email)}
            helperText={
              form.email.length > 0 && !EMAIL_RE.test(form.email)
                ? 'Format email invalide'
                : ' '
            }
            inputProps={{ inputMode: 'email', autoComplete: 'email' }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Téléphone"
            required
            fullWidth
            value={form.phone}
            onChange={handleChange('phone')}
            placeholder="06 12 34 56 78"
            error={form.phone.length > 0 && phoneDigits(form.phone).length !== 10}
            helperText={
              form.phone.length > 0 && phoneDigits(form.phone).length !== 10
                ? '10 chiffres requis'
                : 'Format 06 12 34 56 78'
            }
            inputProps={{
              inputMode: 'numeric',
              autoComplete: 'tel',
              maxLength: 14,
            }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Nombre d'adultes"
            type="number"
            required
            fullWidth
            value={form.adults}
            onChange={handleChange('adults')}
            inputProps={{ min: 1, max: 75, step: 1 }}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            label="Nombre d'enfants"
            type="number"
            required
            fullWidth
            value={form.children}
            onChange={handleChange('children')}
            inputProps={{ min: 0, max: 75, step: 1 }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Alert
            icon={<Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>🌭</Box>}
            severity="info"
            variant="outlined"
            sx={{
              py: 0,
              px: 1,
              height: '100%',
              alignItems: 'center',
              '& .MuiAlert-message': { py: 0.25, fontSize: '0.8rem' },
              '& .MuiAlert-icon': { py: 0.25, mr: 0.75 },
            }}
          >
            Pas fan des moules ? Des <strong>saucisses sont prévues à volonté</strong> pour ceux qui préfèrent.
          </Alert>
        </Grid>
        <Grid item xs={12} md={9}>
          <TextField
            label="Commentaire (facultatif)"
            fullWidth
            value={form.comment}
            onChange={handleChange('comment')}
            inputProps={{ maxLength: 200 }}
            helperText={`${form.comment.length}/200`}
            FormHelperTextProps={{ sx: { mt: 0.25, mx: 1 } }}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              height: '100%',
              px: { xs: 0, md: 1 },
            }}
          >
            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              Total : <strong>{Number(form.adults || 0) + Number(form.children || 0)} repas</strong>
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>{error}</Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mt: 1.5 }}>{success}</Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1.5}
        sx={{ mt: 2 }}
      >
        <Link
          component={RouterLink}
          to="/retrouver"
          underline="hover"
          color="text.secondary"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 14 }}
        >
          <SearchIcon fontSize="small" />
          Vous avez déjà préréservé ? Retrouver mon récap
        </Link>
        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={<SendIcon />}
          disabled={submitting || closed || full || !isFormValid}
        >
          {submitting ? 'Envoi en cours…' : closed || full ? 'Complet' : 'Préréserver'}
        </Button>
      </Stack>
    </Paper>
  );
}
